import { StorageAuthError, StorageError } from '../../errors.js';
import { LAYOUTS, sanitizeFolderName } from '../../layouts.js';
import { isBlobNamespace } from '../../namespaces.js';
import { deleteFile, getFileBlob, getFileText, updateJson, uploadBlob, uploadJson, } from './drive-client.js';
import { createFolder, renameFile } from './drive-folders.js';
import { DriveIndex } from './drive-index.js';
import { effectiveDisplayName, pathForRecord } from './drive-paths.js';
import { clearStoredToken, getValidToken, revokeGoogleToken } from './oauth-gis.js';
const DEFAULT_SCOPE = 'https://www.googleapis.com/auth/drive.file';
const PROVIDER_ID = 'google-drive';
export class GoogleDriveProvider {
    id = PROVIDER_ID;
    displayName = 'Google Drive';
    clientId;
    scope;
    folderId;
    index;
    /** Cache of (namespace:key) → file id for nested-layout records. */
    recordFileByKey = new Map();
    /** Dedup in-flight folder-segment ensures so concurrent writes don't race
     *  into creating duplicate folders at the same path. */
    inFlightSegments = new Map();
    /** Serialize sidecar writes per path so rapid save events overwrite rather
     *  than producing duplicate files in Drive. */
    sidecarWriteQueue = new Map();
    constructor(opts) {
        this.clientId = opts.clientId;
        this.scope = opts.scope ?? DEFAULT_SCOPE;
        this.folderId = opts.folderId;
        this.index = opts.folderId ? new DriveIndex(opts.folderId) : null;
    }
    async isReady() {
        if (!this.clientId || !this.folderId)
            return false;
        return (await getValidToken(this.clientId, this.scope)) !== null;
    }
    async getRecord(ns, key) {
        const { token } = await this.requireContext();
        const fileId = await this.resolveRecordFile(token, ns, key);
        if (!fileId)
            return null;
        try {
            return JSON.parse(await getFileText(token, fileId));
        }
        catch {
            return null;
        }
    }
    async putRecord(rec) {
        const { token, index } = await this.requireContext();
        const path = pathForRecord(rec.namespace, rec.key, effectiveDisplayName(rec), rec.recordFileName);
        const parentId = await this.ensureFolderPath(token, path.folderSegments);
        const filePath = [...path.folderSegments, path.fileName].join('/');
        const content = JSON.stringify(rec);
        let fileId = await index.resolve(token, filePath);
        if (!fileId && path.layout.kind === 'nested') {
            fileId = this.recordFileByKey.get(compoundKey(rec.namespace, rec.key)) ?? null;
        }
        if (fileId) {
            await updateJson(token, fileId, content);
        }
        else {
            fileId = await uploadJson(token, parentId, path.fileName, content);
            index.set(filePath, fileId);
        }
        this.recordFileByKey.set(compoundKey(rec.namespace, rec.key), fileId);
        if (rec.sidecars && rec.sidecars.length > 0) {
            await this.writeSidecars(token, parentId, path.folderSegments, rec.sidecars);
        }
    }
    async deleteRecord(ns, key, deletedAt) {
        const existing = await this.getRecord(ns, key);
        await this.putRecord({
            namespace: ns,
            key,
            value: existing?.value ?? null,
            updatedAt: existing?.updatedAt ?? deletedAt,
            deletedAt,
            version: existing?.version,
            displayName: existing?.displayName,
            recordFileName: existing?.recordFileName,
        });
    }
    async listRefs(ns) {
        const { token } = await this.requireContext();
        const layout = LAYOUTS[ns];
        if (layout.kind === 'blob')
            return [];
        const fileIds = await this.discoverRecordFileIds(token, ns);
        const out = [];
        for (const fileId of fileIds) {
            try {
                const env = JSON.parse(await getFileText(token, fileId));
                if (env.namespace !== ns)
                    continue;
                this.recordFileByKey.set(compoundKey(env.namespace, env.key), fileId);
                out.push({
                    namespace: env.namespace,
                    key: env.key,
                    updatedAt: env.updatedAt,
                    deletedAt: env.deletedAt,
                });
            }
            catch {
                // skip unreadable
            }
        }
        return out;
    }
    async getBlob(ns, key) {
        if (!isBlobNamespace(ns))
            return null;
        const { token, index } = await this.requireContext();
        const path = pathForRecord(ns, key);
        const fileId = await index.resolve(token, [...path.folderSegments, path.fileName].join('/'));
        return fileId ? getFileBlob(token, fileId) : null;
    }
    async putBlob(ns, key, blob) {
        if (!isBlobNamespace(ns))
            return;
        const { token, index } = await this.requireContext();
        const path = pathForRecord(ns, key);
        const parentId = await this.ensureFolderPath(token, path.folderSegments);
        const filePath = [...path.folderSegments, path.fileName].join('/');
        const existing = await index.resolve(token, filePath);
        if (existing)
            await deleteFile(token, existing);
        const newId = await uploadBlob(token, parentId, path.fileName, blob);
        index.set(filePath, newId);
    }
    async getSidecar(ns, key, sidecarName) {
        const { token, index } = await this.requireContext();
        const env = await this.getRecord(ns, key);
        if (!env)
            return null;
        const path = pathForRecord(ns, key, effectiveDisplayName(env), env.recordFileName);
        const sidecarPath = [...path.folderSegments, sidecarName].join('/');
        const fileId = await index.resolve(token, sidecarPath);
        return fileId ? getFileBlob(token, fileId) : null;
    }
    async signOut() {
        const token = await getValidToken(this.clientId, this.scope);
        if (token)
            await revokeGoogleToken(token.accessToken);
        clearStoredToken();
        this.index?.clear();
        this.recordFileByKey.clear();
    }
    invalidateCache() {
        this.index?.clear();
        this.recordFileByKey.clear();
        this.inFlightSegments.clear();
        this.sidecarWriteQueue.clear();
    }
    /**
     * Walk nested-layout subfolders and rename anything whose name drifted from
     * the envelope's desired form — both the containing folder (uses
     * `effectiveDisplayName`) and the inner JSON record file (uses
     * `env.recordFileName` with fallback to the layout default). Idempotent: a
     * second run after everything is correct is a no-op. Folder-name collisions
     * are skipped to avoid duplicate sibling folders.
     */
    async reconcile(ns) {
        if (!this.folderId || !this.index)
            return;
        const layout = LAYOUTS[ns];
        if (layout.kind !== 'nested')
            return;
        const { token } = await this.requireContext();
        const root = layout.parentFolder;
        const defaultRecName = layout.recordFileName ?? 'record.json';
        const subfolders = await this.index.children(token, root);
        const existing = new Set(subfolders);
        let renamed = false;
        for (const sub of subfolders) {
            const folderPath = `${root}/${sub}`;
            const folderId = await this.index.resolve(token, folderPath);
            if (!folderId)
                continue;
            const innerNames = await this.index.children(token, folderPath);
            const currentRecName = innerNames.find((n) => n.endsWith('.json'));
            if (!currentRecName)
                continue;
            const fileId = await this.index.resolve(token, `${folderPath}/${currentRecName}`);
            if (!fileId)
                continue;
            let env;
            try {
                env = JSON.parse(await getFileText(token, fileId));
            }
            catch {
                continue;
            }
            const desiredRecName = env.recordFileName ?? defaultRecName;
            if (desiredRecName !== currentRecName) {
                try {
                    await renameFile(token, fileId, desiredRecName);
                    renamed = true;
                }
                catch {
                    // best-effort
                }
            }
            const desiredFolder = sanitizeFolderName(effectiveDisplayName(env) ?? env.key);
            if (desiredFolder !== sub && !existing.has(desiredFolder)) {
                try {
                    await renameFile(token, folderId, desiredFolder);
                    existing.delete(sub);
                    existing.add(desiredFolder);
                    renamed = true;
                }
                catch {
                    // best-effort
                }
            }
        }
        if (renamed) {
            this.index.clear();
            this.recordFileByKey.clear();
        }
    }
    async resolveRecordFile(token, ns, key) {
        const layout = LAYOUTS[ns];
        if (layout.kind === 'blob')
            return null;
        if (layout.kind === 'flat') {
            const path = `${layout.parentFolder}/${sanitizeFolderName(key)}.json`;
            return this.index.resolve(token, path);
        }
        const cached = this.recordFileByKey.get(compoundKey(ns, key));
        if (cached)
            return cached;
        // Cache miss — walk subfolders to find which study.json holds this key.
        const fileIds = await this.discoverRecordFileIds(token, ns);
        for (const fileId of fileIds) {
            try {
                const env = JSON.parse(await getFileText(token, fileId));
                this.recordFileByKey.set(compoundKey(env.namespace, env.key), fileId);
                if (env.namespace === ns && env.key === key)
                    return fileId;
            }
            catch {
                // skip
            }
        }
        return null;
    }
    async discoverRecordFileIds(token, ns) {
        const layout = LAYOUTS[ns];
        if (layout.kind === 'blob')
            return [];
        const root = layout.parentFolder;
        const out = [];
        if (layout.kind === 'flat') {
            const names = await this.index.children(token, root);
            for (const name of names) {
                if (!name.endsWith('.json'))
                    continue;
                const id = await this.index.resolve(token, `${root}/${name}`);
                if (id)
                    out.push(id);
            }
            return out;
        }
        // nested
        const subfolders = await this.index.children(token, root);
        const recName = layout.recordFileName ?? 'record.json';
        for (const sub of subfolders) {
            const id = await this.index.resolve(token, `${root}/${sub}/${recName}`);
            if (id)
                out.push(id);
        }
        return out;
    }
    async ensureFolderPath(token, segments) {
        let path = '';
        let current = this.folderId;
        for (const seg of segments) {
            path = path ? `${path}/${seg}` : seg;
            current = await this.ensureSegment(token, current, seg, path);
        }
        return current;
    }
    /** Ensures a single folder segment exists, deduping concurrent callers. */
    ensureSegment(token, parentId, segment, path) {
        const inFlight = this.inFlightSegments.get(path);
        if (inFlight)
            return inFlight;
        const promise = (async () => {
            let id = await this.index.resolve(token, path);
            if (!id) {
                id = await createFolder(token, segment, parentId);
                this.index.set(path, id);
            }
            return id;
        })();
        this.inFlightSegments.set(path, promise);
        void promise.finally(() => this.inFlightSegments.delete(path));
        return promise;
    }
    async writeSidecars(token, parentId, folderSegments, sidecars) {
        for (const sc of sidecars) {
            const sidecarPath = [...folderSegments, sc.name].join('/');
            const prior = this.sidecarWriteQueue.get(sidecarPath) ?? Promise.resolve();
            const next = prior.then(() => this.writeOneSidecar(token, parentId, sc, sidecarPath));
            this.sidecarWriteQueue.set(sidecarPath, next);
            void next.finally(() => {
                if (this.sidecarWriteQueue.get(sidecarPath) === next) {
                    this.sidecarWriteQueue.delete(sidecarPath);
                }
            });
            await next;
        }
    }
    async writeOneSidecar(token, parentId, sc, sidecarPath) {
        const existing = await this.index.resolve(token, sidecarPath);
        if (existing) {
            await deleteFile(token, existing);
            this.index.remove(sidecarPath);
        }
        const newId = await uploadBlob(token, parentId, sc.name, sc.blob);
        this.index.set(sidecarPath, newId);
    }
    async requireContext() {
        if (!this.folderId || !this.index) {
            throw new StorageError('Drive folder not configured', PROVIDER_ID, 'context');
        }
        const token = await getValidToken(this.clientId, this.scope);
        if (!token)
            throw new StorageAuthError(PROVIDER_ID, 'auth');
        return { token: token.accessToken, index: this.index };
    }
}
function compoundKey(ns, key) {
    return `${ns}:${key}`;
}
//# sourceMappingURL=google-drive-provider.js.map